// English source strings for identity federation and provisioning (ADR-011, module 17).
export const enIdentity = {
  // ------------------------------------------------------------ API messages
  'api.ssoFailed': 'Single sign-on could not be completed. Try again, or sign in with your Passage password.',
  'api.ssoNotLinked': 'Your organisation account is not linked to Passage yet. Ask your Passage administrator for access.',
  'api.ssoUnavailable': 'This identity provider cannot be reached with its current settings.',
  'api.providerNotTestable': 'This identity provider needs a complete configuration, and must not be disabled, before it can be tested.',
  'api.providerTestRequired': 'Run a successful test sign-in with the current settings before activating this provider.',
  'api.identityLinkExists': 'This external identity is already linked, or the user already has a link with this provider.',
  'api.identityLinkRevoked': 'This link has already been removed.',

  // ------------------------------------------------------------ validation
  'v.url': 'Enter a full address, for example https://login.example.com.',
  'v.httpsRequired': 'Use an https:// address.',
  'v.claimName': 'Use a claim or attribute name of letters, digits and : . / # _ - only.',
  'v.entraTenant': 'Enter a Microsoft Entra tenant ID (a GUID).',
  'v.certificate': 'Paste a PEM certificate or public key.',
  'v.mappingElevated': 'Administrator roles, specialist roles and sensitive capabilities can be mapped only with “Allow elevated access” selected.',
  'v.secretShort': 'Use at least 8 characters.',
  'v.secretRef': 'Use the secret’s name in capitals, digits and underscores (for example LDAP_BIND_PASSWORD).',
  'v.ldapsUrl': 'Use an ldaps:// address (LDAP over TLS).',
  'v.simulatorProduction': 'The directory simulator is available in development only.',
  'v.json': 'Enter valid JSON.',

  // ------------------------------------------------------------ navigation and tabs
  'identity.tab': 'Identity providers',
  'roles.tab': 'Role preview',
  'identity.sub': 'Single sign-on, provisioning and directory synchronisation for your organisation.',
  'identity.panel': 'Single sign-on & provisioning',
  'identity.panelSub': 'Sign-in proves who someone is; what they may do still comes only from Passage grants.',
  'identity.simulatorNote': 'Only local simulators have been tested so far. Real identity-provider and directory interoperability has not been proven yet.',
  'identity.entitlementMissing': 'Your organisation’s plan does not include this capability. Existing settings stay visible and can be disabled.',
  'identity.loading': 'Loading identity settings…',

  // ------------------------------------------------------------ providers
  'identity.providers': 'Identity providers',
  'identity.providersSub': 'Microsoft Entra ID, other OpenID Connect providers, and SAML 2.0.',
  'identity.providers.none': 'No identity provider is configured. People sign in with their Passage password.',
  'identity.providers.add': 'Add identity provider',
  'identity.col.provider': 'Provider',
  'identity.col.protocol': 'Protocol',
  'identity.col.status': 'Status',
  'identity.col.tested': 'Tested',
  'identity.col.linked': 'Linked people',
  'identity.col.action': 'Action',
  'identity.open': 'Open',
  'identity.openLabel': 'Open {name}',
  'identity.tested.yes': 'Current settings tested',
  'identity.tested.no': 'Not tested since last change',
  'identity.protocol.oidc': 'OpenID Connect',
  'identity.protocol.saml': 'SAML 2.0',
  'identity.preset.entra': 'Microsoft Entra ID',
  'identity.preset.generic': 'Standards-based',
  'status.draft': 'Draft',
  'status.held': 'Held for review',

  'identity.add.title': 'Add identity provider',
  'identity.add.sub': 'The provider starts as a draft. Test it before activating it.',
  'identity.add.kind': 'Provider type',
  'identity.kind.entra-oidc': 'Microsoft Entra ID (OpenID Connect)',
  'identity.kind.oidc': 'Other OpenID Connect provider',
  'identity.kind.saml': 'SAML 2.0 provider',
  'identity.kind.entra-saml': 'Microsoft Entra ID (SAML)',
  'identity.f.displayName': 'Name shown on the sign-in page',
  'identity.f.tenantIds': 'Approved Microsoft Entra tenant IDs',
  'identity.f.tenantIdsHint': 'Sign-ins from any other tenant are refused. Separate several IDs with commas.',
  'identity.f.issuer': 'Issuer',
  'identity.f.authorizationEndpoint': 'Authorization endpoint',
  'identity.f.tokenEndpoint': 'Token endpoint',
  'identity.f.jwksUri': 'Signing keys (JWKS) address',
  'identity.f.clientId': 'Client (application) ID',
  'identity.f.clientSecret': 'Client secret',
  'identity.f.clientSecretHint': 'Stored encrypted and never shown again. Rotation is recorded by version only.',
  'identity.f.groupsClaim': 'Groups claim',
  'identity.f.idpEntityId': 'Identity provider entity ID',
  'identity.f.ssoUrl': 'Single sign-on address',
  'identity.f.certificate': 'Signing certificate (PEM)',
  'identity.f.certificateHint': 'Paste the identity provider’s signing certificate or public key.',
  'identity.f.linkByEmail': 'Let existing Passage users link their account by confirming their Passage password once',
  'identity.f.trustIdpMfa': 'Accept the identity provider’s multi-factor authentication instead of Passage’s own',
  'identity.f.linkProvisioned': 'Link automatically when a provisioned external ID equals the verified subject',
  'identity.f.jit': 'Create accounts on first sign-in (no access is granted except by mapping rules)',
  'identity.f.jitDomains': 'Email domains allowed for automatic accounts',
  'identity.create': 'Create draft',

  // ------------------------------------------------------------ provider detail
  'identity.detail.eyebrow': 'Passage / Users & permissions / Identity providers',
  'identity.detail.back': 'Back to identity providers',
  'identity.detail.facts': 'Provider',
  'identity.detail.version': 'Configuration version',
  'identity.detail.secret': 'Client secret',
  'identity.detail.secretSet': 'Configured (version {version})',
  'identity.detail.secretNone': 'Not configured',
  'identity.detail.register': 'Register Passage at your identity provider',
  'identity.detail.registerSub': 'Copy these values into the provider’s application settings.',
  'identity.detail.entityId': 'Entity ID (audience)',
  'identity.detail.acsUrl': 'Reply / assertion consumer address',
  'identity.detail.redirectUri': 'Redirect URI',
  'identity.detail.metadataUrl': 'Service-provider metadata',
  'identity.detail.certificates': 'Trusted signing certificates',
  'identity.detail.certificatesSub': 'Add the new certificate before your provider rotates, then remove the old one.',
  'identity.detail.fingerprint': 'SHA-256 fingerprint',
  'identity.detail.validTo': 'Valid until',
  'identity.detail.publicKey': 'Public key',
  'identity.detail.addCertificate': 'Add certificate',
  'identity.detail.removeCertificate': 'Remove',
  'identity.detail.removeCertificateLabel': 'Remove certificate {fingerprint}',
  'identity.detail.test': 'Test sign-in',
  'identity.detail.testHint': 'Signs in at the provider without creating a Passage session, and shows what a real sign-in would do.',
  'identity.detail.activate': 'Activate',
  'identity.detail.activateSub': 'People will see this provider on the sign-in page.',
  'identity.detail.disable': 'Disable',
  'identity.detail.disableSub': 'Sign-in through this provider stops and sessions started with it end now.',
  'identity.detail.rotate': 'Rotate client secret',
  'identity.detail.rotateSub': 'The new secret is used from the next sign-in. The audit records the version only.',
  'identity.detail.lastTest': 'Last test',
  'identity.detail.never': 'Never',

  // ------------------------------------------------------------ mapping rules
  'mapping.title': 'Claim mapping',
  'mapping.sub': 'Grant Passage access from claims the provider sends. Applied at each sign-in; access ends when the claim no longer matches.',
  'mapping.scimTitle': 'Group mapping',
  'mapping.scimSub': 'Grant Passage access from SCIM group membership. Applied immediately; removed when membership ends.',
  'mapping.none': 'No mapping rules. Access comes only from grants issued in Passage.',
  'mapping.add': 'Add mapping rule',
  'mapping.col.when': 'When',
  'mapping.col.grant': 'Grants',
  'mapping.col.conferred': 'People',
  'mapping.when': '{claim} = {value}',
  'mapping.whenGroup': 'Member of {group}',
  'mapping.f.claim': 'Claim or attribute',
  'mapping.f.value': 'Value',
  'mapping.f.group': 'SCIM group',
  'mapping.f.kind': 'Grant type',
  'mapping.kind.experience': 'Role',
  'mapping.kind.specialist': 'Specialist role',
  'mapping.f.code': 'Role',
  'mapping.f.site': 'Site',
  'mapping.f.elevated': 'Allow elevated access (administrator or specialist roles)',
  'mapping.f.elevatedHint': 'Only people who may assign sensitive powers can save this. Prefer assigning these roles individually.',
  'mapping.elevatedBadge': 'Elevated',
  'mapping.disable': 'Disable',
  'mapping.disableSub': 'Grants that only this rule justifies are revoked now.',

  // ------------------------------------------------------------ test result
  'test.title': 'Test sign-in result',
  'test.loading': 'Checking the test sign-in…',
  'test.invalid': 'This test result is not available. Start a new test from the provider page.',
  'test.outcome': 'Outcome',
  'test.reason': 'Reason',
  'test.subject': 'Verified subject',
  'test.email': 'Email',
  'test.emailVerified': 'Email verified',
  'test.groups': 'Groups',
  'test.idpMfa': 'Provider asserted multi-factor authentication',
  'test.wouldResolve': 'A real sign-in would',
  'test.resolve.linked_user': 'Sign in the linked Passage account',
  'test.resolve.link_with_password': 'Ask the matching Passage user to confirm their password once, then link',
  'test.resolve.provisioned_subject': 'Link the provisioned account with this external ID',
  'test.resolve.jit_create': 'Create a new account (just-in-time)',
  'test.resolve.no_account': 'Refuse: no Passage account is linked',
  'test.matchedUser': 'Passage account',
  'test.grants': 'Access from mapping rules',
  'test.noGrants': 'None',
  'test.backToProvider': 'Back to the provider',
  'test.reason.state_invalid': 'The sign-in request was not recognised or was already used.',
  'test.reason.request_expired': 'The request expired or the provider settings changed during the test.',
  'test.reason.signature_invalid': 'The signature could not be verified with the trusted keys.',
  'test.reason.unsigned': 'The response was not signed.',
  'test.reason.issuer_mismatch': 'The response came from a different issuer.',
  'test.reason.audience_mismatch': 'The response was meant for a different application.',
  'test.reason.recipient_mismatch': 'The response was addressed to a different reply address.',
  'test.reason.expired': 'The response had expired.',
  'test.reason.not_yet_valid': 'The response is not valid yet (check clocks).',
  'test.reason.tenant_not_allowed': 'The Microsoft Entra tenant is not on the approved list.',
  'test.reason.replayed': 'This response had already been used.',
  'test.reason.token_exchange_failed': 'The token endpoint refused the request (check client ID and secret).',
  'test.reason.provider_unavailable': 'The provider could not be reached.',
  'test.reason.other': 'The response was refused ({reason}).',

  // ------------------------------------------------------------ SCIM
  'scim.title': 'SCIM provisioning',
  'scim.sub': 'Your identity provider creates, updates and deactivates Passage accounts. Accounts are never deleted.',
  'scim.baseUrl': 'SCIM base URL',
  'scim.provisioned': 'Accounts managed by SCIM',
  'scim.tokens': 'Access tokens',
  'scim.tokens.none': 'No tokens issued.',
  'scim.token.issue': 'Issue token',
  'scim.token.issueSub': 'The token is shown once. Store it in your identity provider’s provisioning settings.',
  'scim.token.label': 'Label',
  'scim.token.expires': 'Valid for (days)',
  'scim.token.hint': 'Ends with',
  'scim.token.lastUsed': 'Last used',
  'scim.token.revoke': 'Revoke',
  'scim.token.revokeSub': 'Provisioning with this token stops immediately.',
  'scim.token.issued': 'Token issued',
  'scim.token.issuedBody': 'Copy it now. Passage keeps only a fingerprint and cannot show it again.',
  'scim.groups': 'Provisioned groups',
  'scim.groups.none': 'No groups provisioned yet.',
  'scim.groupMembers': { one: '{count} member', other: '{count} members' },

  // ------------------------------------------------------------ directory
  'directory.title': 'Directory synchronisation',
  'directory.sub': 'Read-only synchronisation from LDAP / Active Directory. If the directory is unavailable, nothing changes and the last known state is kept.',
  'directory.none': 'No directory connection.',
  'directory.add': 'Add directory',
  'directory.kind.simulator': 'Directory simulator (development only)',
  'directory.kind.ldap': 'LDAP / Active Directory',
  'directory.f.secretRef': 'Bind credential secret name',
  'directory.f.secretRefHint': 'The name of the deployment secret holding the password — never the password itself.',
  'directory.f.entries': 'Synthetic directory entries (JSON)',
  'directory.f.schedule': 'Synchronise every (minutes)',
  'directory.f.maxDeactivation': 'Hold for review if more than this % of accounts would be disabled',
  'directory.lastRun': 'Last run',
  'directory.lastSuccess': 'Last successful read',
  'directory.owned': 'Accounts owned',
  'directory.stale': 'Stale: the directory has not been read successfully recently. Accounts keep their last known state.',
  'directory.sync': 'Synchronise now',
  'directory.syncSub': 'Reads the directory and applies changes to the accounts it owns.',
  'directory.confirm': 'Apply held deactivations',
  'directory.confirmSub': 'The last run was held because it would disable many accounts. Review the directory before applying.',
  'directory.run.result': 'Created {created} · updated {updated} · disabled {deactivated} · re-enabled {reactivated} · unchanged {unchanged} · conflicts {conflicts}',
  'directory.error.unreachable': 'Directory unreachable',
  'directory.error.adapter_not_available': 'LDAP adapter not available yet',
  'directory.error.credential_missing': 'Bind credential not set, not found or cannot be opened',
  'directory.error.invalid_response': 'Unexpected directory response',
  'directory.conflict.email_in_use': 'Email already used by another account',
  'directory.conflict.duplicate_external_id': 'Duplicate external ID in the directory',
  'directory.conflict.invalid_entry': 'Invalid directory entry',
  'directory.conflict.deactivation_threshold': 'Deactivations held for review',
  'directory.conflict.last_administrator': 'Would remove the last administrator',
  'directory.conflict.administrator_hold': 'Disabled by an administrator in Passage',

  // ------------------------------------------------------------ linked identities on a user
  'links.title': 'Sign-in identities',
  'links.sub': 'External identities that can sign in as this person, and the source that provisions the account.',
  'links.none': 'No external identity linked.',
  'links.provider': 'Provider',
  'links.subject': 'Subject',
  'links.via': 'Linked by',
  'links.via.local_proof': 'Password confirmation',
  'links.via.administrator': 'Administrator',
  'links.via.provisioned_subject': 'Provisioned external ID',
  'links.via.jit': 'Automatic account',
  'links.source': 'Provisioned by',
  'links.source.none': 'Passage (managed here)',
  'links.source.scim': 'SCIM',
  'links.source.directory': 'Directory',
  'links.source.sso_jit': 'Single sign-on (automatic account)',
  'links.add': 'Link identity',
  'links.addSub': 'Enter the stable subject your provider uses for this person (for Microsoft Entra ID: tenant ID/object ID).',
  'links.revoke': 'Unlink',
  'links.revokeSub': 'Sign-in with this identity stops and sessions started with it end now.',

  // ------------------------------------------------------------ sign-in
  'auth.ssoWith': 'Continue with {name}',
  'auth.ssoRedirecting': 'Opening your organisation’s sign-in…',
  'sso.title': 'Single sign-on',
  'sso.checking': 'Completing sign-in…',
  'sso.failed': 'Single sign-on could not be completed. Try again, or sign in with your Passage password.',
  'sso.link.title': 'Link your Passage account',
  'sso.link.lede': '{provider} signed you in as {email}. Confirm your Passage password once to link this sign-in to your account.',
  'sso.link.submit': 'Confirm and link',
  'sso.back': 'Back to sign-in',

  // ------------------------------------------------------------ role preview (PB-17-01)
  'roles.title': 'Role preview',
  'roles.sub': 'What each role can do, generated from the server’s authorization catalogue. Anything not listed is denied.',
  'roles.loading': 'Loading the role catalogue…',
  'roles.col.action': 'Action',
  'roles.col.scope': 'Scope',
  'roles.scope.tenant': 'Organisation',
  'roles.scope.site': 'Site',
  'roles.sensitive': 'Sensitive — specialist role only',
  'roles.entitlement': 'Requires {entitlement}',
  'roles.tableLabel': 'Role and action matrix',
  'roles.specialists': 'Specialist roles',
  'roles.yes': 'Included',

  // ------------------------------------------------------------ system console
  'platform.identity.title': 'Identity federation',
  'platform.identity.sub': 'Read-only summary. Organisation administrators configure providers; you can disable one in an emergency.',
  'platform.identity.providers': 'Identity providers',
  'platform.identity.none': 'No identity providers.',
  'platform.identity.tokens': 'SCIM tokens',
  'platform.identity.directories': 'Directory connections',
  'platform.identity.elevated': 'Active elevated mapping rules',
  'platform.identity.provisioned': 'Provisioned accounts: SCIM {scim} · directory {directory} · automatic {jit}',
  'platform.identity.disable': 'Emergency disable',
} as const;
